import { useHistory } from "react-router-dom";
import { usePageLinks } from "../../app/hooks/usePageLinks";
import { useConversationEngine } from "../../assistant/useConversationEngine";
import type { QuestionOrigin } from "./useGptChat";
import type { ArticleDetail } from "../../../features/article/hooks/useArticleData";
import { gaEventArticleChatQuestion } from "../../../tools/analytics/domains/article.analytics";

/**
 * Hands a question about one paper to the engine that answers it. The assistant
 * engine has no Chat with paper: it asks in a new conversation that references
 * the paper, which the conversation then names "About this study". The legacy
 * engine keeps Chat with paper, which asks the question on arrival.
 *
 * Until the engine is known the legacy route is taken: the flag resolves while
 * the page loads, long before a reader has typed a question.
 */
export function useAskAboutPaper(): (
  paper: ArticleDetail,
  question: string,
  origin: QuestionOrigin
) => void {
  const history = useHistory();
  // Not room-scoped, like the legacy chat button.
  const { chatWithPaperLink, gptNewChatPageLink } = usePageLinks(null);
  const engine = useConversationEngine();
  return (paper, question, origin) => {
    switch (engine) {
      case "assistant":
        // Chat with paper reports the question when it asks it. Here no page
        // knows the paper's details after the hand-off, so the question is
        // reported as it leaves: the first question of a new conversation.
        gaEventArticleChatQuestion({
          article: paper,
          question,
          conversationHistory: "new",
          questionNumber: 1,
          questionOrigin: origin,
          engine: "assistant",
        });
        history.push(gptNewChatPageLink(), {
          initialQuestion: question,
          initialQuestionOrigin: origin,
          initialArticle: { id: paper.id, title: paper.title },
        });
        return;
      case "legacy":
      case "pending":
        history.push(chatWithPaperLink(paper), {
          initialQuestion: question,
          initialQuestionOrigin: origin,
        });
        return;
      default:
        throw new Error(engine satisfies never);
    }
  };
}
